import { z } from 'zod';
import { eventSchema } from './envelope';

export const FLOOR_REPORTS_SUBMITTED_V1 = 'floor_reports.report.submitted.v1' as const;
export const FLOOR_REPORTS_STATUS_CHANGED_V1 = 'floor_reports.report.status_changed.v1' as const;

/**
 * Er is iets gemeld vanaf de vloer.
 *
 * **Zonder de beschrijving en zonder de foto's** (ADR-0038): een melding van een bijna-ongeval
 * beschrijft wat er bijna gebeurde en met wie, en dat is precies het soort tekst dat niet in een
 * eventspoor hoort. Wel mee: het type ("Bijna-ongeval") en de zone, want dat is wat de behandelaar
 * in zijn melding moet zien om te weten of hij nu moet gaan kijken.
 *
 * `reporterId` is `null` bij een anonieme melding, en dat is geen weglating in dit event: er
 * bestáát geen melder-id (ADR-0062). Een event dat hem wél zou dragen, zou de anonimiteit ongedaan
 * maken in het eventspoor — dat `platform.outbox` voor altijd bewaart.
 */
export const reportSubmittedV1Schema = eventSchema({
  reportId: z.guid(),
  typeId: z.guid(),
  typeName: z.string(),
  zoneName: z.string().nullable(),
  reporterId: z.guid().nullable(),
  /** Wie dit hoort op te volgen. `notifications` kan die vraag zelf niet beantwoorden. */
  handlerIds: z.array(z.guid()),
});
export type ReportSubmittedV1 = z.infer<typeof reportSubmittedV1Schema>;

/**
 * De stand van een melding is gewijzigd.
 *
 * Dit is de terugkoppeling aan de melder (acceptatiecriterium 3), en daarom gaat `reporterId` mee
 * — bij een anonieme melding is die `null` en krijgt dus niemand een melding. Dat is de prijs van
 * anonimiteit en ze wordt vooraf gezegd.
 *
 * De **opmerking** gaat niet mee. Die kan "je hebt de verkeerde machine gemeld" zijn, en dat is
 * iets om in de melding te lezen en niet op een vergrendeld scherm.
 */
export const reportStatusChangedV1Schema = eventSchema({
  reportId: z.guid(),
  typeName: z.string(),
  fromStatus: z.enum(['new', 'in_progress', 'resolved', 'rejected']),
  toStatus: z.enum(['new', 'in_progress', 'resolved', 'rejected']),
  reporterId: z.guid().nullable(),
  changedBy: z.guid(),
});
export type ReportStatusChangedV1 = z.infer<typeof reportStatusChangedV1Schema>;

export const floorReportsEventCatalog = {
  [FLOOR_REPORTS_SUBMITTED_V1]: reportSubmittedV1Schema,
  [FLOOR_REPORTS_STATUS_CHANGED_V1]: reportStatusChangedV1Schema,
} as const;
